import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ErroApi } from '../../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, cnpjFormatado, mascaraCpfCnpj,
} from '../../componentes/ui';
import type { Escritorio } from '../../tipos';

interface Sistema { id: number; nome: string; escritorios: number }

export function Escritorios() {
  const [params, setParams] = useSearchParams();
  const sistema = params.get('sistema') ? Number(params.get('sistema')) : undefined;
  const semSistema = params.get('semSistema') === '1';
  const [lista, setLista] = useState<Escritorio[] | null>(null);
  const [sistemas, setSistemas] = useState<Sistema[]>([]);
  const [semSistemaTotal, setSemSistemaTotal] = useState(0);
  const [erro, setErro] = useState('');
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Escritorio | null>(null);

  async function carregar() {
    try {
      const { escritorios } = await api.escritorios({ sistema, semSistema });
      setLista(escritorios);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os escritórios.');
    }
  }

  useEffect(() => { void carregar(); }, [sistema, semSistema]);
  useEffect(() => {
    api.visaoGeral()
      .then((v) => { setSistemas(v.porSistema); setSemSistemaTotal(v.escritoriosSemEmpresa); })
      .catch(() => {});
  }, []);

  function filtrarPor(novoSistema: number | null, novoSemSistema: boolean) {
    const p = new URLSearchParams();
    if (novoSistema) p.set('sistema', String(novoSistema));
    if (novoSemSistema) p.set('semSistema', '1');
    setParams(p);
  }

  async function alternarAtivo(e: Escritorio) {
    try {
      await api.alterarEscritorio(e.id, { ativo: !e.ativo });
      await carregar();
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não consegui alterar.');
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Escritórios</h1>
          <p>
            O acesso mora aqui, não na pessoa. Funcionário entra e sai do escritório sem ninguém
            remexer em vínculo de empresa.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setNovo(true)}>Novo escritório</button>
      </header>

      {(sistemas.length > 0 || semSistemaTotal > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <button className={`btn btn-sm ${!sistema && !semSistema ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => filtrarPor(null, false)}>
            Todos
          </button>
          {sistemas.map((s) => (
            <button key={s.id} className={`btn btn-sm ${sistema === s.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => filtrarPor(s.id, false)}>
              {s.nome} ({s.escritorios})
            </button>
          ))}
          {semSistemaTotal > 0 && (
            <button className={`btn btn-sm ${semSistema ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => filtrarPor(null, true)}>
              Sem sistema ({semSistemaTotal})
            </button>
          )}
        </div>
      )}

      <Erro>{erro}</Erro>

      {!lista ? <Carregando /> : lista.length === 0 ? (
        <Vazio
          titulo={sistema || semSistema ? 'Nenhum escritório neste filtro' : 'Nenhum escritório ainda'}
          descricao="O escritório é o dono do acesso às empresas — e é ele que a instalação usa para cadastrar empresa sozinha."
          acao={<button className="btn btn-primary btn-sm" onClick={() => setNovo(true)}>Criar o primeiro</button>}
        />
      ) : (
        <Tabela
          itens={lista}
          chaveDe={(e) => e.id}
          classeLinha={(e) => (e.ativo ? undefined : 'linha-inativa')}
          colunas={[
            { rotulo: 'Nome', principal: true, celula: (e) => <strong>{e.nome}</strong> },
            { rotulo: 'CPF/CNPJ', fraco: true, mono: true, celula: (e) => (e.cnpj ? cnpjFormatado(e.cnpj) : '—') },
            { rotulo: 'Empresas', numero: true, celula: (e) => e.empresas ?? '—' },
            { rotulo: 'Pessoas', numero: true, celula: (e) => e.pessoas ?? '—' },
            { rotulo: 'Guarda', numero: true, fraco: true, celula: (e) => `${e.retencaoMeses} meses` },
            {
              rotulo: 'Ações',
              acoes: true,
              celula: (e) => (
                <>
                  {!e.ativo && <Selo tipo="inativo" />}
                  <button className="link-botao" onClick={() => setEditando(e)}>Editar</button>
                  <button className="link-botao" onClick={() => alternarAtivo(e)}>
                    {e.ativo ? 'Desativar' : 'Reativar'}
                  </button>
                </>
              ),
            },
          ]}
        />
      )}

      {novo && (
        <FormularioEscritorio
          onFechar={() => setNovo(false)}
          onSalvo={() => { setNovo(false); void carregar(); }}
        />
      )}
      {editando && (
        <FormularioEscritorio
          escritorio={editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => { setEditando(null); void carregar(); }}
        />
      )}
    </div>
  );
}

function FormularioEscritorio({
  escritorio, onFechar, onSalvo,
}: { escritorio?: Escritorio; onFechar: () => void; onSalvo: () => void }) {
  const [nome, setNome] = useState(escritorio?.nome ?? '');
  const [cnpj, setCnpj] = useState(escritorio?.cnpj ?? '');
  const [meses, setMeses] = useState(escritorio?.retencaoMeses ?? 60);
  const [observacao, setObservacao] = useState(escritorio?.observacao ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      if (escritorio) {
        await api.alterarEscritorio(escritorio.id, { nome, retencaoMeses: meses, observacao: observacao || null });
      } else {
        await api.criarEscritorio({ nome, cnpj: cnpj || undefined, retencaoMeses: meses, observacao: observacao || undefined });
      }
      onSalvo();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo={escritorio ? 'Editar escritório' : 'Novo escritório'}
      descricao={escritorio ? undefined : 'É a ele que as empresas e as pessoas vão ficar penduradas.'}
      onFechar={onFechar}
    >
      <Erro>{erro}</Erro>
      <Campo rotulo="Nome">
        <input className="input" value={nome} onChange={(e) => setNome(e.target.value)}
          placeholder="Contabilidade Vale" autoFocus />
      </Campo>
      {escritorio ? (
        <Campo rotulo="CPF ou CNPJ" ajuda="Não dá pra trocar por aqui — é a chave que a softhouse usa para achar o escritório.">
          <input className="input" value={cnpj ? cnpjFormatado(cnpj) : '—'} disabled />
        </Campo>
      ) : (
        <Campo
          rotulo="CPF ou CNPJ"
          ajuda="Opcional — serve para achar o escritório depois, inclusive contador autônomo (CPF)."
        >
          <input className="input" value={cnpj} onChange={(e) => setCnpj(mascaraCpfCnpj(e.target.value))}
            placeholder="000.000.000-00 ou 00.000.000/0000-00" inputMode="numeric" />
        </Campo>
      )}
      <Campo
        rotulo="Prazo de guarda (meses)"
        ajuda="Quanto tempo o portal guarda os documentos deste escritório. O padrão legal é 60 meses; o prazo é decisão do contador."
      >
        <input className="input" type="number" min={12} value={meses}
          onChange={(e) => setMeses(Number(e.target.value))} />
      </Campo>
      <Campo
        rotulo="Observação"
        ajuda="Texto livre — responsável, telefone extra, o que não tem campo próprio."
      >
        <textarea className="input" value={observacao} onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex.: responsável Fulana de Tal" />
      </Campo>

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || nome.trim().length < 2}>
          {salvando ? <span className="spinner" /> : escritorio ? 'Salvar' : 'Criar escritório'}
        </button>
      </div>
    </Modal>
  );
}
